import { Alert, Box, Button, CircularProgress, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import ThumbnailBox from "./ThumbnailBox";

interface Props {
  name: string;
  content: string;
  creating: boolean;
  onNameChange: (value: string) => void;
  onContentChange: (value: string) => void;
  onCancel: () => void;
  onCreate: () => void;
  translationLanguageCount?: number;
}

const AddTemplateForm = ({
  name,
  content,
  creating,
  onNameChange,
  onContentChange,
  onCancel,
  onCreate,
  translationLanguageCount = 0,
}: Props) => {
  const { t } = useTranslation("message");
  const translationText =
    translationLanguageCount > 0
      ? t(
          translationLanguageCount === 1
            ? "settings.form.translationText"
            : "settings.form.translationTextPlural",
          { count: translationLanguageCount },
        )
      : "";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        p: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
        <TextField
          label={t("settings.form.templateName")}
          value={name}
          disabled={creating}
          onChange={(e) => onNameChange(e.target.value)}
          sx={{ flex: 1 }}
        />
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={3}
        label={t("settings.form.templateContent")}
        value={content}
        disabled={creating}
        onChange={(e) => onContentChange(e.target.value)}
        sx={{ mb: 1.5 }}
      />
      {creating && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {t("settings.form.creatingTemplate", { translationText })}
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button size="small" onClick={onCancel} disabled={creating}>
          {t("settings.cancel")}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onCreate}
          disabled={creating || !name.trim() || !content.trim()}
        >
          {creating ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              {translationLanguageCount > 0
                ? t("settings.form.creatingAndTranslating")
                : t("settings.form.creating")}
            </>
          ) : (
            t("settings.create")
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default AddTemplateForm;
